import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
    getInfoGraphics, 
    getPublications, 
    getRadioIllustrations, 
    getAlbumById, 
    setAlbum, 
    deleteAlbum, 
    likeAlbum, 
    uploadAlbumPhoto 
} from '../Services/ApiCalls';

export const useAlbums = (category: 'InfoGraphics' | 'Publications' | 'RadioIllustrations' | 'all', page = 1, pageSize = 10) => {
    return useQuery({
        queryKey: ['albums', category, page, pageSize],
        queryFn: () => {
            if (category === 'InfoGraphics') return getInfoGraphics(page, pageSize);
            if (category === 'Publications') return getPublications(page, pageSize);
            if (category === 'RadioIllustrations') return getRadioIllustrations(page, pageSize);
            return getInfoGraphics(page, pageSize); // default fallback
        }
    });
};

export const useAlbumById = (id: number) => {
    return useQuery({
        queryKey: ['album', id],
        queryFn: () => getAlbumById(id),
        enabled: !!id
    });
};

export const useCreateAlbum = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ title, description, coverImage, uploadedBy, category }: any) => 
            setAlbum(title, description, coverImage, uploadedBy, category),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['albums'] });
        }
    });
};

export const useDeleteAlbum = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id: number) => deleteAlbum(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['albums'] });
        }
    });
};

export const useLikeAlbum = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, action }: { id: number, action: 'like' | 'unlike' }) => likeAlbum(id, action),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['album', variables.id] });
            queryClient.invalidateQueries({ queryKey: ['albums'] });
        }
    });
};

export const useUploadAlbumPhoto = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ albumId, file, imagedesc }: { albumId: string, file: File, imagedesc: string }) => 
            uploadAlbumPhoto(albumId, file, imagedesc),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['album', Number(variables.albumId)] });
        }
    });
};
